import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import LazyLoad from 'vanilla-lazyload';

gsap.registerPlugin(Draggable, InertiaPlugin);

export class ImageCarousel {
    PREFIX = 'image-carousel';
    PREFIX_NAVIGATION = 'carousel';
    MIN_SLIDES = 10;
    _setDefaultFirst = true;
    _activeTitle = null;
    _transitioning = false;
    _activeDot = null;
    _startRotation = 0;

    // Parameters
    GAP = 10;

    /**
     * @param {Element} domElm
     */
    constructor (domElm) {
        this.domElm = domElm;
        this.carouselContainer = this.domElm.querySelector(`.${this.PREFIX}__slides`) ?? this.domElm;
        this.carouselDragger = this.domElm.querySelector(`.${this.PREFIX}__dragger`) ?? this.domElm;

        if (Array.from(this.slides).length > 0) {
            const slides = Array.from(this.slides);
            while (this.slides.length < this.MIN_SLIDES) {
                for (let slide of slides) {
                    const clone = slide.cloneNode(true);
                    clone.setAttribute('aria-hidden', 'true');
                    clone.copyOf = slide;
                    this.carouselContainer.appendChild(clone);
                }
            }

            this.addNavigation();
            this.addListeners();
            this.init();
        }
    }

    get startRotation () {
        return this._startRotation;
    }

    set startRotation (value) {
        this._startRotation = value;
    }

    get nbSlides () {
        return Array.from(this.slides).filter(slide => slide.getAttribute('aria-hidden') === null).length;
    }

    get nbTotalSlides () {
        return Array.from(this.slides).length;
    }

    get slideWidth () {
        return this.slides[0].offsetWidth + this.GAP;
    }

    get dotNavigation () {
        return this.domElm.querySelector(`.${this.PREFIX_NAVIGATION}__dot-navigation`);
    }

    get anglePerImageDeg () {
        return 360 / this.nbTotalSlides;
    }

    get anglePerImageRad () {
        return 2 * Math.PI / this.nbTotalSlides;
    }

    get degPerPx () {
        return this.anglePerImageDeg / this.slideWidth;
    }

    get RFromVisible () {
        return this.slideWidth / (2 * Math.sin(this.anglePerImageRad / 2));
    }

    getAdjacentSlide (direction = 1) {
        let current = this.activeElement;

        for (let i = 0; i < Math.abs(direction); i++) {
            if (direction > 0) {
                current = current.previousElementSibling ?? this.slides[this.slides.length - 1];
            } else if (direction < 0) {
                current = current.nextElementSibling ?? this.slides[0];
            }
        }

        return current;
    }

    set activeTitle (value) {
        this._activeTitle?.classList.remove('active');
        value.classList.add('active');
        this._activeTitle = value;
    }

    get setDefaultFirst () {
        return this._setDefaultFirst;
    }

    get carouselContainer () {
        return this._carouselContainer;
    }

    set carouselContainer (value) {
        this._carouselContainer = value;
    }

    set isAutomation (value) {
        this._isAutomation = value;
    }

    get slides () {
        return this.carouselContainer.children;
    }

    get dots () {
        return this.dotNavigation.children;
    }

    get domElm () {
        return this._domElm;
    }

    set domElm (value) {
        this._domElm = value;
    }

    get activeElement () {
        return this._activeElement;
    }

    get autoplayTimer () {
        return this._autoplayTimer;
    }

    get navigation () {
        return this._navigation;
    }

    set navigation (value) {
        this._navigation = value;
    }

    get prevBtn () {
        return this._prevBtn;
    }

    set prevBtn (value) {
        this._prevBtn = value;
    }

    get nextBtn () {
        return this._nextBtn;
    }

    set nextBtn (value) {
        this._nextBtn = value;
    }

    set autoplayTimer (value) {
        this._autoplayTimer = value;
    }

    set activeElement (value) {
        if (value === null) return;

        this._activeElement?.classList.remove('active');
        value?.classList.add('active');

        this._activeElement = value;

        this.activeDot = this.dotNavigation.querySelector('[data-linked-id=\'' + this.activeElement.dataset.elmId + '\']');
    }

    set activeDot (value) {
        if (value === null) return;

        this._activeDot?.classList.remove('active');
        value?.classList.add('active');

        this._activeDot = value;
    }

    get activeDot () {
        return this._activeDot;
    }

    init () {
        if (this.setDefaultFirst) {
            this.activeElement = this.slides[0];
        }

        this.domElm.classList.add('initialized');

        this.updateNavigation();

        // GSAP INITIALISATION
        gsap.set(this.domElm, { perspective: this.RFromVisible });

        gsap.timeline()
            .set(this.carouselContainer, { rotationY: Math.round(0 / this.anglePerImageDeg) * this.anglePerImageDeg })
            .set(this.carouselContainer, { rotationX: 0 })
            .set(this.carouselContainer, { translateZ: this.RFromVisible })
            .set(`.${this.PREFIX}__slide`, { // apply transform rotations to each image
                rotateY: (i) => i * -this.anglePerImageDeg,
                transformOrigin: `50% 50% ${this.RFromVisible}px`,
                z: -this.RFromVisible,
                backfaceVisibility: 'hidden'
            })
            .from(`.${this.PREFIX}__slide`, {
                duration: 1,
                stagger: 0.1,
                ease: 'expo'
            });

        // Load all images
        window.addEventListener('load', () => {
            this.domElm.querySelectorAll('img.lazy').forEach(function (el) {
                LazyLoad.load(el, { use_native: true });
            });
        });
    }

    setRingRotation (newRot) {
        return gsap.quickSetter(this.carouselContainer, 'rotationY', 'deg')(newRot);
    }

    addListeners () {
        // Add navigation listener if present
        if (this.navigation) {
            this.prevBtn?.addEventListener('click', () => {
                if (!this._transitioning)
                    this.prev();
            });

            this.nextBtn?.addEventListener('click', () => {
                if (!this._transitioning)
                    this.next();
            });
        }

        // GSAP Listeners
        const [dragger] = Draggable.create(this.carouselDragger, {
            type: 'x',
            inertia: true,
            onPress: (e) => {
                if (e.touches) e.clientX = e.touches[0].clientX;
                this.startRotation = gsap.getProperty(this.carouselContainer, 'rotationY');

                // Si snap en cours, interrompre proprement
                gsap.killTweensOf(this.carouselContainer);
                this._transitioning = false;
            },

            onDrag: () => {
                const newRot = this.startRotation - (dragger.x * this.degPerPx);
                this.setRingRotation(newRot);
            },

            onThrowUpdate: () => {
                const newRot = this.startRotation - (dragger.x * this.degPerPx);
                this.setRingRotation(newRot);
            },

            onThrowComplete: () => {
                this.snapToNearest();
            }
        });
    }

    snapToNearest () {
        const currentRot = gsap.getProperty(this.carouselContainer, 'rotationY');
        const snappedIndex = Math.round(currentRot / this.anglePerImageDeg);
        const snapped = snappedIndex * this.anglePerImageDeg;

        this._transitioning = true;

        gsap.to(this.carouselContainer, {
            rotationY: snapped,
            duration: 0.5,
            ease: 'power3.out',
            onComplete: () => {
                this.startRotation = snapped;
                gsap.set(this.carouselDragger, { x: 0 });
                this.activeElement = this.slides[Math.abs(snappedIndex) % this.nbSlides];
                this._transitioning = false;
            }
        });
    }

    goTo (index) {
        if (this._transitioning) return;

        const total = this.nbTotalSlides;
        const wrappedIndex = ((index % total) + total) % total; // wrap autour de 0..total-1
        const targetRotation = wrappedIndex * this.anglePerImageDeg;

        this._transitioning = true;
        gsap.to(this.carouselContainer, {
            rotationY: targetRotation,
            duration: 0.5,
            ease: 'power3.out',
            onComplete: () => {
                this.startRotation = targetRotation;
                gsap.set(this.carouselDragger, { x: 0 });
                this.activeElement = this.slides[wrappedIndex];
                this._transitioning = false;
            }
        });
    }

    addNavigation () {
        // Retrieve navigation
        this.navigation = this.domElm.querySelector(`.${this.PREFIX_NAVIGATION}__navigation`);

        // Test if navigation is present
        if (this.navigation) {
            this.prevBtn = this.navigation.querySelector(`.${this.PREFIX_NAVIGATION}__navigation-prev`);
            this.nextBtn = this.navigation.querySelector(`.${this.PREFIX_NAVIGATION}__navigation-next`);
        }

        this.generateDots();
    }

    updateNavigation () {
        if (this.nextBtn)
            this.nextBtn.disabled = !this.isNextAvailable();

        if (this.prevBtn)
            this.prevBtn.disabled = !this.isPreviousAvailable();
    }

    isPreviousAvailable () {
        // TODO
        return true;
    }

    isNextAvailable () {
        return !this.checkMaxReached();
    }

    next () {
        const currentRot = gsap.getProperty(this.carouselContainer, 'rotationY');
        const currentIndex = Math.round(currentRot / this.anglePerImageDeg);

        this.goTo(currentIndex + 1);
    }

    prev () {
        const currentRot = gsap.getProperty(this.carouselContainer, 'rotationY');
        const currentIndex = Math.round(currentRot / this.anglePerImageDeg);
        this.goTo(currentIndex - 1);
    }

    checkMaxReached () {
        return false;
    }

    generateDots () {
        for (let i = 0; i < this.nbSlides; i++) {
            const slide = this.slides[i];
            const dot = document.createElement('div');
            dot.classList.add('dot');
            dot.setAttribute('data-linked-id', slide.dataset.elmId);
            this.dotNavigation.prepend(dot);
        }
    }
}
